import type { ConsentObj } from "../../../app/pages/invoices/consent/interface";
import {
  clearAnalyticsSession,
  enrichEventProps,
  setAnalyticsSession,
} from "./context";
import * as fullstory from "./fullstory";
import * as mixpanel from "./mixpanel";
import * as posthog from "./posthog";
import type { AnalyticsProps } from "./props";
import { PAYER_EVENT_TAXONOMY } from "./taxonomy";

export type AnalyticsConfig = {
  fullstoryOrgId?: string;
  posthogKey?: string;
  posthogHost?: string;
  mixpanelToken?: string;
};

/** Init every configured destination. Missing keys are skipped. */
export function initAnalytics(config: AnalyticsConfig) {
  fullstory.initFullStory({ orgId: config.fullstoryOrgId });
  posthog.initPostHog({
    apiKey: config.posthogKey,
    apiHost: config.posthogHost,
  });
  mixpanel.initMixpanel({ token: config.mixpanelToken });
}

function withPayerContext(
  event: string,
  properties?: AnalyticsProps
): AnalyticsProps {
  return enrichEventProps(event, properties, {
    actor: "payer",
    surface: "checkout",
    taxonomyByEvent: PAYER_EVENT_TAXONOMY,
  });
}

export function track(event: string, properties?: AnalyticsProps) {
  const props = withPayerContext(event, properties);
  fullstory.track(event, props);
  posthog.track(event, props);
  mixpanel.track(event, props);
}

export function identify(userId: string, traits?: AnalyticsProps) {
  fullstory.identify(userId, traits);
  posthog.identify(userId, traits);
  mixpanel.identify(userId, traits);

  const organizationId = traits?.organization_id;
  if (organizationId !== undefined && organizationId !== null) {
    groupMerchant(String(organizationId), {
      name: traits?.organization_name,
    });
  }
}

export function groupMerchant(merchantId: string, traits?: AnalyticsProps) {
  if (!merchantId) return;
  posthog.group("merchant", merchantId, traits);
  mixpanel.group("merchant", merchantId, traits);
  setAnalyticsSession({
    merchant_id: merchantId,
    organization_id: merchantId,
    ...traits,
  });
}

export function page(name?: string, properties?: AnalyticsProps) {
  const props = enrichEventProps(
    name || "Page View",
    { taxonomy_id: "P3", ...properties },
    {
      actor: "payer",
      surface: "checkout",
      taxonomyByEvent: PAYER_EVENT_TAXONOMY,
    }
  );
  fullstory.page(name, props);
  posthog.page(name, props);
  mixpanel.page(name, props);
}

export function reset() {
  clearAnalyticsSession();
  fullstory.reset();
  posthog.reset();
  mixpanel.reset();
}

export function buildPayerContext(
  consentForm?: Partial<ConsentObj> | null
): AnalyticsProps {
  return fullstory.buildPayerContext(consentForm);
}

export function trackPayer(
  event: string,
  properties?: AnalyticsProps,
  consentForm?: Partial<ConsentObj> | null
) {
  const payerContext = buildPayerContext(consentForm);
  if (payerContext.organization_id) {
    groupMerchant(String(payerContext.organization_id), {
      name: payerContext.organization_name,
    });
  }
  setAnalyticsSession({
    merchant_id: payerContext.organization_id,
    organization_id: payerContext.organization_id,
    organization_name: payerContext.organization_name,
    payment_link_id: payerContext.payment_link_id,
    consent_id: payerContext.consent_id,
    invoice_id: payerContext.invoice_id,
  });

  track(event, {
    ...payerContext,
    ...properties,
  });
}
